import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointVerifyIdentityDataInput,
  type EnrollmentJointVerifyIdentityData,
  type EnrollmentJointVerifyIdentitySubmitInput,
  type EnrollmentJointVerifyIdentitySubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint VerifyIdentity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyIdentityData>
 * @example const response = await getEnrollmentJointVerifyIdentityData()
 */
export const getEnrollmentJointVerifyIdentityData = async (
  enrollmentVerifyIdentitySubmitInput?: EnrollmentJointVerifyIdentityDataInput,
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyIdentityData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointVerifyIdentityData',
      query: `mutation getEnrollmentJointVerifyIdentityData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            applicationId
            path
            progress
            name {
              firstName
              lastName
            }
            dateOfBirth
            residencyStatus
            citizenshipCountries
          }
        }
      }`,
    },
    {
      input: enrollmentVerifyIdentitySubmitInput ?? {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyIdentityData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointVerifyIdentityData = createQueryHook<
  EnrollmentJointVerifyIdentityDataInput,
  EnrollmentJointVerifyIdentityData
>(getEnrollmentJointVerifyIdentityData)

/**
 * Submit Enrollment Joint VerifyIdentity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyIdentitySubmitOutput>
 * @example const response = await submitEnrollmentJointVerifyIdentityData();
 */
export const submitEnrollmentJointVerifyIdentityData = async (
  enrollmentVerifyIdentitySubmitInput: EnrollmentJointVerifyIdentitySubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyIdentitySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointVerifyIdentityData',
      query: `mutation submitEnrollmentJointVerifyIdentityData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentVerifyIdentitySubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyIdentitySubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointVerifyIdentityData = createMutationHook<
  EnrollmentJointVerifyIdentitySubmitInput,
  EnrollmentJointVerifyIdentitySubmitOutput
>(submitEnrollmentJointVerifyIdentityData)
